/**
 * Food row component for admin foods table.
 */

import * as stylex from "@stylexjs/stylex";
import { useCallback } from "react";

import { useZero } from "@animus/frontend-features/zero";
import { getFoodGroupLabel } from "@animus/habit-modules/nutrition";
import { badge, button, layout, table, text } from "@animus/primaris";
import { mutators } from "@animus/zero-schema";

import type { NutritionFood } from "./types.ts";
import { EditableMacroCell } from "./editable-macro-cell.tsx";
import { styles } from "./styles.stylex.ts";

function formatTimestamp(timestamp: number | Date): string {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return date.toLocaleString();
}

interface FoodRowProps {
  food: NutritionFood;
  onViewDetails: () => void;
}

function FoodRow({ food, onViewDetails }: FoodRowProps) {
  const z = useZero();
  const foodGroupLabel = getFoodGroupLabel(food.foodGroup);

  const handleMacroSave = useCallback(
    (field: "calories" | "protein" | "carbs" | "fat", value: number) => {
      z.mutate(
        mutators.habit_nutrition_food.updateMacro({
          id: food.id,
          field,
          value,
        })
      );
    },
    [z, food.id]
  );

  return (
    <tr {...stylex.props(table.tr)}>
      <td {...stylex.props(table.td)}>
        <div>
          <p {...stylex.props(text.white)}>{food.name}</p>
          <p {...stylex.props(text.sm, text.muted)}>{food.normalizedName}</p>
        </div>
      </td>
      <EditableMacroCell
        onSave={(v) => handleMacroSave("calories", v)}
        value={food.calories}
      />
      <EditableMacroCell
        onSave={(v) => handleMacroSave("protein", v)}
        suffix="g"
        value={food.protein}
      />
      <EditableMacroCell
        onSave={(v) => handleMacroSave("carbs", v)}
        suffix="g"
        value={food.carbs}
      />
      <EditableMacroCell
        onSave={(v) => handleMacroSave("fat", v)}
        suffix="g"
        value={food.fat}
      />
      <td {...stylex.props(table.td)}>
        <span {...stylex.props(badge.base, badge.blue)}>{foodGroupLabel}</span>
      </td>
      <td {...stylex.props(table.td)}>
        <div {...stylex.props(styles.tagContainer)}>
          {(food.tags ?? []).slice(0, 3).map((tag) => (
            <span
              {...stylex.props(badge.base, badge.neutral, styles.tag)}
              key={tag}
            >
              {tag}
            </span>
          ))}
          {(food.tags ?? []).length > 3 && (
            <span {...stylex.props(text.sm, text.muted)}>
              +{(food.tags ?? []).length - 3}
            </span>
          )}
        </div>
      </td>
      <td {...stylex.props(table.td)}>
        <span {...stylex.props(text.sm, text.muted)}>
          {food.updatedAt ? formatTimestamp(food.updatedAt) : "—"}
        </span>
      </td>
      <td {...stylex.props(table.td, table.tdRight)}>
        <div {...stylex.props(layout.flexRow, layout.gap2)}>
          <button
            {...stylex.props(button.base, button.secondary, button.small)}
            onClick={onViewDetails}
            type="button"
          >
            View
          </button>
        </div>
      </td>
    </tr>
  );
}

export { FoodRow, type FoodRowProps };
